import React from 'react';
import {
  Paper,
  Button,
  Divider,
  FormControl,
  Box,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
} from '@mui/material';
import {
  AttachFile,
  Audiotrack,
  Description,
  PictureAsPdf,
  Theaters,
  Send,
  CloudUpload,
  Delete as DeleteIcon,
} from '@mui/icons-material/';
import { useParams } from "react-router-dom";
import { DialogoVisualizador } from '../../components';
import api from "../../services/api";
import { useStoreActions } from 'easy-peasy';
import { driveBase } from '../../config';

const EnviaOrcamento = () => {
  let params = useParams();
  let startLoading = useStoreActions((actions) => actions.nav.startLoading);
  let stopLoading = useStoreActions((actions) => actions.nav.stopLoading);
  const [disabled, setDisabled] = React.useState(null);
  const [solicitacao, setSolicitacao] = React.useState(null);
  const [arquivos, setArquivos] = React.useState([]);

  const onSubmit = async () => {
    if (arquivos.length === 0) return;
    startLoading();
    try {
      await api.http.put('/orcamentos-fornecedores/' + params.hash, {
        docOrcamentoIds: arquivos.map((a) => a.id),
      });
      stopLoading({
        message: 'Orçamento enviado com sucesso',
        severity: 'success',
      });
      setDisabled(true);
    } catch (error) {
      stopLoading({
        message: error.toString(),
        severity: 'error',
      });
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      startLoading();
      try {
        const sol = (await api.http.get('/orcamentos-fornecedores/' + params.hash)).data;
        if (cancelled) return;
        setSolicitacao(sol);
        const orc = sol?.orcamento?.[0];
        const lista = orc?.docOrcamentoLista;
        const existentes = lista?.arquivos?.map((item) => item.arquivo) ?? [];
        if (existentes.length > 0) {
          setArquivos(existentes);
          setDisabled(true);
          stopLoading({
            message: 'Orçamento já enviado',
            severity: 'info',
          });
        } else {
          stopLoading();
        }
      } catch (error) {
        if (!cancelled) {
          setDisabled(true);
          stopLoading({ message: error.toString(), severity: 'error' });
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [params.hash]);

  const PreviewIcon = ({ arquivo }) => {
    const mime = arquivo?.mimeType ?? arquivo?.mime_type ?? '';
    if (mime.startsWith('video/')) return <Theaters />;
    if (mime.startsWith('audio/')) return <Audiotrack />;

    switch (mime) {
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return <Description />;
    case "application/pdf":
      return <PictureAsPdf />;
    default:
      return <AttachFile />;
    }
  };

  const [file, setFile] = React.useState(null);
  const [openDlgVis, setOpenDlgVis] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const onCloseDlgVis = () => {
    setFile(null);
    setOpenDlgVis(false);
  };

  const onIconClick = (arquivo) => {
    if (
      arquivo.mimeType === 'application/pdf' ||
      arquivo.mimeType === 'text/html' ||
      arquivo.mimeType === 'text/plain' ||
      arquivo.mimeType === 'application/json' ||
      arquivo.mimeType === 'image/jpeg' ||
      arquivo.mimeType === 'image/png' ||
      arquivo.mimeType === 'image/gif' ||
      arquivo.mimeType === 'image/bmp' ||
      arquivo.mimeType === 'image/webp' ||
      arquivo.mimeType === 'video/mp4'
    ) {
      setFile(arquivo);
      setOpenDlgVis(true);
    } else {
      window.location.href = driveBase + (arquivo.fileRef || arquivo.file_ref);
    }
  };

  const onUploadClick = () => fileInputRef.current?.click();

  const onFileChange = async (event) => {
    if (!event.target.files?.length) return;
    const file = event.target.files[0];
    event.target.value = '';
    const formData = new FormData();
    formData.append('file', file);
    startLoading();
    try {
      const uploaded = (
        await api.http.post('/orcamentos-fornecedores/' + params.hash, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      ).data;
      setArquivos((prev) => [...prev, uploaded]);
      stopLoading();
    } catch (error) {
      stopLoading({ message: error.toString(), severity: 'error' });
    }
  };

  const removeArquivo = (id) => {
    setArquivos((prev) => prev.filter((a) => a.id !== id));
  };

  const isDirty = arquivos.length > 0;

  return (
    <Paper
      variant="elevation"
      elevation={4}
      className="center-box"
      sx={{
        textAlign: 'center',
        px: 4,
        pt: 1,
        pb: 3,
        m: 5,
        maxWidth: 'sm',
      }}
    >
      <DialogoVisualizador file={file} open={openDlgVis} onClose={onCloseDlgVis} />
      <Divider>
        <h3>Arquivos da solicitação</h3>
      </Divider>
      <List sx={{ width: '100%' }}>
        {solicitacao?.docsSolicitacao?.arquivos?.map((solArquivo, index) => (
          <ListItem disablePadding key={index}>
            <ListItemButton onClick={() => onIconClick(solArquivo.arquivo)}>
              <ListItemIcon>
                <PreviewIcon arquivo={solArquivo.arquivo} />
              </ListItemIcon>
              <ListItemText primary={solArquivo.arquivo.fileName} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider>
        <h3>Envio do orçamento</h3>
      </Divider>
      <FormControl sx={{ my: 2 }} fullWidth>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={onFileChange}
          disabled={disabled}
        />
        <Button
          color="primary"
          variant="outlined"
          onClick={onUploadClick}
          disabled={disabled}
          startIcon={<CloudUpload />}
          sx={{ mb: 2 }}
        >
          Adicionar arquivo de orçamento
        </Button>
        {arquivos.length > 0 && (
          <List dense>
            {arquivos.map((ar) => (
              <ListItem
                key={ar.id}
                secondaryAction={
                  !disabled && (
                    <IconButton
                      edge="end"
                      aria-label="remover"
                      onClick={() => removeArquivo(ar.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )
                }
              >
                <ListItemIcon>
                  <PreviewIcon arquivo={ar} />
                </ListItemIcon>
                <ListItemButton onClick={() => onIconClick(ar)}>
                  <ListItemText primary={ar.fileName || ar.file_name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </FormControl>
      <Box sx={{ mt: 2 }}>
        <Button
          type="button"
          variant="contained"
          size="large"
          endIcon={<Send />}
          disabled={disabled || !isDirty}
          onClick={onSubmit}
        >
          Enviar
        </Button>
      </Box>
    </Paper>
  );
};

export default EnviaOrcamento;
